<script setup>
import { useForm, usePage, router, Head, Link } from '@inertiajs/vue3';
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import { route } from 'ziggy-js';
import axios from 'axios';
import draggable from 'vuedraggable';
import ImageUpload from '@/Components/ImageUpload.vue';

const props = defineProps({
  mobils: Array,
  jenisMobils: Array,
  errors: Object,
  flash: Object
});

const page = usePage();
const isEditing = ref(false);

// Local reactive copy of mobils for partial updates without page reload
const mobils = ref([...props.mobils]);

// Sync props.mobils to local mobils when props change (e.g., after Inertia navigation)
watch(() => props.mobils, (newMobils) => {
    mobils.value = [...newMobils];
}, { deep: true });

// Form Data
const form = useForm({
  id: '',
  jenis_mobil_id: '',
  mobil_nama: '',
  mobil_slug: '',
  mobil_fasilitas: [], // 'Lepas Kunci', 'Include'
  mobil_harga_sewa: '',
  mobil_harga_include: '',
  mobil_detail: '',
  mobil_artikel: '',   // CKEditor
  mobil_foto: null,
  mobil_foto_mobile: null,

  meta_title: '',
  meta_description: '',
});

// CKEditor Instance
let artikelEditor = null;

// Counters
const detailWordCount = ref(0); // For mobil_detail (textarea)
const listingCharCount = ref(0);
const artikelMetaCharCount = ref(0);

// Initialize CKEditors
const initEditors = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }

    if (CKEDITOR.instances['mobil_artikel']) CKEDITOR.instances['mobil_artikel'].destroy();

    // Artikel Editor
    artikelEditor = CKEDITOR.replace('mobil_artikel', {
        height: 300,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList', 'Blockquote'] },
            { name: 'styles', items: ['Format'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'insert', items: ['HorizontalRule'] },
            { name: 'undo', items: ['Undo', 'Redo'] }
        ]
    });

    artikelEditor.on('change', () => {
         form.mobil_artikel = artikelEditor.getData();
         // Manually check validity if needed, but we rely on form submission
    });
};

const updateDetailCounter = () => {
    // Simple word count for regular textarea
    const content = form.mobil_detail || '';
    detailWordCount.value = content.trim() === '' ? 0 : content.trim().split(/\s+/).length;
};

watch(() => form.mobil_detail, () => {
    updateDetailCounter();
});

onMounted(() => {
    nextTick(() => {
        initEditors();
    });
});

// Watch for flash messages from Inertia (server-side redirects with flash)
watch(() => page.props.flash, (flash) => {
    if (flash?.error) {
        nextTick(() => {
            errorModalContent.value = [flash.error];
            modalType.value = 'error';
            const modalEl = document.getElementById('errorModal');
            if (modalEl) {
                const modal = new bootstrap.Modal(modalEl);
                modal.show();
            }
        });
    }
    // Success notifications are silent - no modal popup
}, { immediate: true, deep: true });

// Cleanup CKEditor instances before component unmounts (SPA navigation)
onBeforeUnmount(() => {
    if (artikelEditor) {
        try {
            artikelEditor.destroy();
        } catch (e) {
            // Ignore errors during destroy (iframe may already be gone)
            console.warn('CKEditor destroy warning:', e.message);
        }
        artikelEditor = null;
    }
    
    // Also destroy any orphaned instances
    if (typeof CKEDITOR !== 'undefined') {
        for (let instanceName in CKEDITOR.instances) {
            try {
                CKEDITOR.instances[instanceName].destroy();
            } catch (e) {
                // Ignore
            }
        }
    }
});

// Actions
const errorModalContent = ref([]);
const originalData = ref(null);
const modalType = ref('error');
const saving = ref(false);

// Show modal helper
const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    try {
        const modal = new bootstrap.Modal(document.getElementById('errorModal'));
        modal.show();
    } catch (e) {
        console.error('Modal show error:', e);
    }
};

const submit = async () => {
    saving.value = true;
    
    // Short delay for spinner visibility
    await new Promise(resolve => setTimeout(resolve, 300));

    // Update form data from editors
    if (artikelEditor) form.mobil_artikel = artikelEditor.getData();

    // Frontend validation
    const errors = [];
    if (!form.jenis_mobil_id) {
        errors.push('Jenis Mobil wajib dipilih.');
    }
    if (!form.mobil_nama || form.mobil_nama.trim() === '') {
        errors.push('Nama Mobil wajib diisi.');
    }
    if (!form.mobil_fasilitas || form.mobil_fasilitas.length === 0) {
        errors.push('Opsi Sewa wajib dipilih minimal satu (Lepas Kunci atau Include Driver).');
    }
    // Conditional price validation
    if (form.mobil_fasilitas.includes('Lepas Kunci') && !form.mobil_harga_sewa) {
        errors.push('Harga Lepas Kunci wajib diisi jika opsi Lepas Kunci dipilih.');
    }
    if (form.mobil_fasilitas.includes('Include') && !form.mobil_harga_include) {
        errors.push('Harga Include wajib diisi jika opsi Include Driver dipilih.');
    }
    if (!form.mobil_artikel || form.mobil_artikel.trim() === '' || form.mobil_artikel.trim() === '<p></p>') {
        errors.push('Konten Artikel Lengkap wajib diisi.');
    }
    // Photo required for new entries
    if (!isEditing.value && !form.mobil_foto) {
        errors.push('Foto Desktop wajib diupload.');
    }
    if (!isEditing.value && !form.mobil_foto_mobile) {
        errors.push('Foto Mobile wajib diupload.');
    }

    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }

    // Check for changes in edit mode
    if (isEditing.value) {
        if (originalData.value) {
            // Compare simple fields
            const currentData = {
                jenis_mobil_id: form.jenis_mobil_id,
                mobil_nama: form.mobil_nama,
                mobil_slug: form.mobil_slug,
                mobil_fasilitas: JSON.stringify([...form.mobil_fasilitas].sort()), // Clone then sort
                mobil_harga_sewa: form.mobil_harga_sewa,
                mobil_harga_include: form.mobil_harga_include,
                mobil_detail: form.mobil_detail,
                meta_title: form.meta_title,
                meta_description: form.meta_description,
            };

            const simpleFieldsMatch = JSON.stringify(originalData.value) === JSON.stringify(currentData);
            
            // Check editor dirty state
            const artikelDirty = artikelEditor ? artikelEditor.checkDirty() : false;
            
            // Check if files are selected (if selected, definitely changed)
            const photoDesktopChanged = !!form.mobil_foto;
            const photoMobileChanged = !!form.mobil_foto_mobile;

            if (simpleFieldsMatch && !artikelDirty && !photoDesktopChanged && !photoMobileChanged) {
                saving.value = false;
                showModal('warning', 'Tidak ada perubahan data yang disimpan.');
                return;
            }
        }
    }

    if (isEditing.value) {
        // Store ID before calling route to ensure it's available
        const updateId = form.id;
        if (!updateId) {
            saving.value = false;
            showModal('error', ['ID mobil tidak ditemukan. Silakan refresh halaman dan coba lagi.']);
            return;
        }

        // Use axios for update - only update the row without page reload
        const formData = new FormData();
        formData.append('_method', 'PUT');
        formData.append('jenis_mobil_id', form.jenis_mobil_id);
        formData.append('mobil_nama', form.mobil_nama);
        form.mobil_fasilitas.forEach(f => formData.append('mobil_fasilitas[]', f));
        if (form.mobil_harga_sewa) formData.append('mobil_harga_sewa', form.mobil_harga_sewa);
        if (form.mobil_harga_include) formData.append('mobil_harga_include', form.mobil_harga_include);
        formData.append('mobil_detail', form.mobil_detail);
        formData.append('mobil_artikel', form.mobil_artikel);
        if (form.meta_title) formData.append('meta_title', form.meta_title);
        if (form.meta_description) formData.append('meta_description', form.meta_description);
        if (form.mobil_foto) formData.append('mobil_foto', form.mobil_foto);
        if (form.mobil_foto_mobile) formData.append('mobil_foto_mobile', form.mobil_foto_mobile);

        try {
            const response = await axios.post(route('admin.mobil.update', { id: updateId }), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (response.data.status === 'success') {
                // Update the row in local state without page reload
                const updatedMobil = response.data.data;
                const index = mobils.value.findIndex(m => m.hash_id === updateId);
                if (index !== -1) {
                    mobils.value[index] = updatedMobil;
                }
                resetForm();
            }
        } catch (error) {
            if (error.response?.status === 422) {
                const serverErrors = error.response.data.errors 
                    ? Object.values(error.response.data.errors).flat()
                    : [error.response.data.message || 'Validasi gagal'];
                showModal('error', serverErrors);
            } else {
                showModal('error', [error.response?.data?.message || 'Terjadi kesalahan saat menyimpan.']);
            }
        } finally {
            saving.value = false;
        }
        
    } else {
        // Use Inertia for store - needs page reload to get new data with hash_id
        const options = {
            preserveScroll: true,
            forceFormData: true,
            onSuccess: () => {
                saving.value = false;
                resetForm();
            },
            onError: (errors) => {
                saving.value = false;
                showModal('error', Object.values(errors).flat());
            },
            onFinish: () => {
                saving.value = false;
            }
        };
        form.post(route('admin.mobil.store'), options);
    }
};

const edit = (item) => {
    isEditing.value = true;
    modalItem.value = item; // Set modalItem for image display
    form.id = item.hash_id;
    form.jenis_mobil_id = item.jenis_mobil_id;
    form.mobil_nama = item.mobil_nama;
    form.mobil_slug = item.mobil_slug || '';
    
    // Parse fasilitas "Lepas Kunci, Include" string to array
    form.mobil_fasilitas = item.mobil_fasilitas ? item.mobil_fasilitas.split(', ').map(f => f.trim()) : [];
    
    form.mobil_harga_sewa = item.mobil_harga_sewa;
    form.mobil_harga_include = item.mobil_harga_include;
    form.mobil_detail = item.mobil_detail || '';
    form.mobil_artikel = item.mobil_artikel || '';
    
    // Auto-generate meta if empty (uses helper functions defined below)
    form.meta_title = item.meta_title || '';
    form.meta_description = item.meta_description || '';
    
    // Auto-fill meta immediately if empty so it's part of the original form state
    if (!form.meta_title) {
        form.meta_title = generateMetaTitle(item.mobil_nama);
    }
    if (!form.meta_description) {
        form.meta_description = generateMetaDescription(item.mobil_nama, item.mobil_fasilitas);
    }
    
    // Reset file inputs
    form.mobil_foto = null;
    form.mobil_foto_mobile = null;

    // Store original data FROM FORM VALUES (ensures type consistency)
    // We capture this AFTER auto-generated metas are filled,
    // so if user saves immediately without changes, verify passes.
    originalData.value = JSON.parse(JSON.stringify({
        jenis_mobil_id: form.jenis_mobil_id,
        mobil_nama: form.mobil_nama,
        mobil_slug: form.mobil_slug,
        mobil_fasilitas: JSON.stringify([...form.mobil_fasilitas].sort()), // Clone then sort
        mobil_harga_sewa: form.mobil_harga_sewa,
        mobil_harga_include: form.mobil_harga_include,
        mobil_detail: form.mobil_detail,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
    }));

    if (artikelEditor) {
        artikelEditor.setData(item.mobil_artikel || '', function() {
            this.resetDirty();
        });
    }
    
    updateDetailCounter();
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const deleteForm = useForm({});
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);

const confirmDelete = (item) => {
    deleteId.value = item.id;
    deleteName.value = item.mobil_nama;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = () => {
    if (!deleteId.value) return;
    deleteForm.delete(route('admin.mobil.destroy', deleteId.value), {
        preserveScroll: true,
        onSuccess: () => {
            const modalEl = document.getElementById('deleteModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
        }
    });
};

const togglingId = ref(null);

const toggleStatus = async (item) => {
    if (!item.hash_id) {
        showModal('error', ['ID tidak ditemukan. Silakan refresh halaman.']);
        return;
    }
    
    togglingId.value = item.hash_id;
    try {
        const response = await axios.patch(`/admin/mobil/${item.hash_id}/toggle-status`);
        if (response.data.status === 'success' || response.data.mobil_status !== undefined) {
            item.mobil_status = response.data.mobil_status ?? !item.mobil_status;
        }
    } catch (error) {
        console.error('Toggle status error:', error);
        showModal('error', ['Gagal mengubah status.']);
    } finally {
        togglingId.value = null;
    }
};

// Drag and Drop for reordering with vuedraggable
const sortableMobils = ref([...mobils.value]);
const isSavingOrder = ref(false);

// Watch for mobils changes to update sortable list
watch(() => mobils.value, (newMobils) => {
    sortableMobils.value = [...newMobils];
}, { deep: true });

const onSortEnd = async () => {
    isSavingOrder.value = true;
    const orderedIds = sortableMobils.value.map((item, index) => ({
        hash_id: item.hash_id,
        urutan: index + 1
    }));
    
    try {
        await axios.post(route('admin.mobil.updateOrder'), { order: orderedIds });
        // Silent success
    } catch (error) {
        console.error('Save order error:', error);
        showModal('error', ['Gagal menyimpan urutan.']);
    } finally {
        isSavingOrder.value = false;
    }
};

// Toggle logic for checkpoints
const hasFacility = (facility) => form.mobil_fasilitas.includes(facility);

// Helper for currency format (display in table)
const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(value);
};

// Helper for input display with thousand separators
const formatThousand = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('id-ID').format(value);
};

// Parse formatted string back to number
const parseThousand = (formattedValue) => {
    if (!formattedValue) return '';
    // Remove all non-digit characters
    const cleaned = String(formattedValue).replace(/[^\d]/g, '');
    return cleaned ? parseInt(cleaned, 10) : '';
};

// Refs for formatted display values
const displayHargaSewa = ref('');
const displayHargaInclude = ref('');

// Watch form values to update display
watch(() => form.mobil_harga_sewa, (newVal) => {
    displayHargaSewa.value = formatThousand(newVal);
}, { immediate: true });

watch(() => form.mobil_harga_include, (newVal) => {
    displayHargaInclude.value = formatThousand(newVal);
}, { immediate: true });

// Handle input changes
const onHargaSewaInput = (event) => {
    const raw = parseThousand(event.target.value);
    form.mobil_harga_sewa = raw;
    displayHargaSewa.value = formatThousand(raw);
};

const onHargaIncludeInput = (event) => {
    const raw = parseThousand(event.target.value);
    form.mobil_harga_include = raw;
    displayHargaInclude.value = formatThousand(raw);
};

// Helper for image URLs
const getImageUrl = (path) => {
    if (!path) return null;
    // If path already contains folder prefix, use as-is; otherwise prepend 'mobil/'
    return path.includes('/') ? `/storage/${path}` : `/storage/mobil/${path}`;
};

// Slug logic
const slugify = (text) => {
  return text.toString().toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
};

// Generate meta title with 55 character limit
const generateMetaTitle = (mobilName) => {
    const brandName = page.props.settings?.setting_title || 'Gotransport';
    let title = `Sewa Mobil ${mobilName} di Banyuwangi - ${brandName}`;
    
    // If exceeds 55 chars, try without brand name
    if (title.length > 55) {
        title = `Sewa Mobil ${mobilName} di Banyuwangi`;
    }
    
    // If still exceeds, truncate
    if (title.length > 55) {
        title = title.substring(0, 52) + '...';
    }
    
    return title;
};

// Generate meta description with 155 character limit
const generateMetaDescription = (mobilName, fasilitas) => {
    const hasLepasKunci = fasilitas?.includes('Lepas Kunci');
    let desc = `Layanan sewa mobil ${mobilName} di Banyuwangi dengan fasilitas ${hasLepasKunci ? 'lepas kunci' : 'driver profesional'}. Unit terawat dan harga kompetitif.`;
    
    if (desc.length > 155) {
        desc = desc.substring(0, 152) + '...';
    }
    
    return desc;
};

watch(() => form.mobil_nama, (newValue) => {
    if (!isEditing.value && newValue) {
         form.mobil_slug = slugify(newValue);
         form.meta_title = generateMetaTitle(newValue);
         form.meta_description = generateMetaDescription(newValue, form.mobil_fasilitas);
    }
});

const imageUploadKey = ref(0);

const resetForm = () => {
    isEditing.value = false;
    modalItem.value = {};
    originalData.value = null;
    form.reset();
    form.clearErrors();
    form.mobil_fasilitas = []; // Explicit reset for array
    if (artikelEditor) artikelEditor.setData('');
    detailWordCount.value = 0;
    
    // Force re-render ImageUpload
    imageUploadKey.value++;
};

// View Modal
const modslTitle = ref('');
const modalItem = ref({});
const viewArtikel = (item) => {
    modslTitle.value = 'Detail Artikel - ' + item.mobil_nama;
    modalItem.value = item;
    const modal = new bootstrap.Modal(document.getElementById('viewModal'));
    modal.show();
};
</script>

<template>
    <Head title="Data Mobil" />
    <div class="container-full">
        <!-- Breadcrumb Section -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Data Mobil</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link></li>
                                <li class="breadcrumb-item active" aria-current="page">Data Mobil</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Section -->
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-car me-2"></i>Form Data Mobil</h4>
                        </div>
                        <form @submit.prevent="submit">
                            <div class="box-body">
                                <div class="row">
                                    <!-- Left Column: Specs & Images -->
                                    <div class="col-lg-6 col-12">
                                        
                                        <!-- Jenis & Nama -->
                                        <div class="row">
                                            <div class="col-md-6 mb-3">
                                                <label for="jenis_mobil_id" class="form-label">Jenis Mobil <span class="text-danger">*</span></label>
                                                <select id="jenis_mobil_id" name="jenis_mobil_id" v-model="form.jenis_mobil_id" class="form-control form-select" :class="{ 'is-invalid': form.errors.jenis_mobil_id }" autocomplete="off">
                                                    <option value="" disabled>Pilih Jenis</option>
                                                    <option v-for="jenis in jenisMobils" :key="jenis.id" :value="jenis.id">{{ jenis.jenis_nama }}</option>
                                                </select>
                                                <div class="invalid-feedback">{{ form.errors.jenis_mobil_id }}</div>
                                            </div>
                                            <div class="col-md-6 mb-3">
                                                <label for="mobil_nama" class="form-label">Nama Mobil <span class="text-danger">*</span></label>
                                                <input type="text" id="mobil_nama" name="mobil_nama" v-model="form.mobil_nama" class="form-control" :class="{ 'is-invalid': form.errors.mobil_nama }" placeholder="Nama unit" autocomplete="off">
                                                <div class="invalid-feedback">{{ form.errors.mobil_nama }}</div>
                                            </div>
                                        </div>

                                        <div class="form-group mb-3">
                                            <label for="mobil_slug" class="form-label">Slug</label>
                                            <input type="text" id="mobil_slug" name="mobil_slug" v-model="form.mobil_slug" class="form-control" :class="{ 'is-invalid': form.errors.mobil_slug }" autocomplete="off">
                                            <div class="invalid-feedback">{{ form.errors.mobil_slug }}</div>
                                        </div>

                                        <!-- Images -->
                                        <div class="row">
                                            <div class="col-md-6 mb-3">
                                                <ImageUpload 
                                                    :key="imageUploadKey"
                                                    label="Foto Desktop (Wajib)" 
                                                    field="mobil_foto" 
                                                    :current-url="getImageUrl(isEditing && !form.mobil_foto ? modalItem.mobil_foto : null)" 
                                                    @update:file="file => form.mobil_foto = file" 
                                                    height="200px"
                                                />
                                                <div class="text-danger small mt-1" v-if="form.errors.mobil_foto">{{ form.errors.mobil_foto }}</div>
                                            </div>
                                            <div class="col-md-6 mb-3">
                                                <ImageUpload 
                                                    :key="imageUploadKey"
                                                    label="Foto Mobile (Wajib)" 
                                                    field="mobil_foto_mobile" 
                                                    :current-url="getImageUrl(isEditing && !form.mobil_foto_mobile ? modalItem.mobil_foto_mobile : null)"
                                                    @update:file="file => form.mobil_foto_mobile = file" 
                                                    height="200px"
                                                />
                                                <div class="text-danger small mt-1" v-if="form.errors.mobil_foto_mobile">{{ form.errors.mobil_foto_mobile }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Right Column: Options & Meta -->
                                    <div class="col-lg-6 col-12">
                                        <!-- Fasilitas Checkboxes -->
                                        <!-- Fasilitas Checkboxes -->
                                        <div class="form-group mb-3">
                                            <div class="form-label d-block">Opsi Sewa <span class="text-danger">*</span></div>
                                            <div class="form-check form-check-inline">
                                                <input class="form-check-input" type="checkbox" id="lepas_kunci" name="mobil_fasilitas" value="Lepas Kunci" v-model="form.mobil_fasilitas">
                                                <label class="form-check-label" for="lepas_kunci">Lepas Kunci</label>
                                            </div>
                                            <div class="form-check form-check-inline">
                                                <input class="form-check-input" type="checkbox" id="include" name="mobil_fasilitas" value="Include" v-model="form.mobil_fasilitas">
                                                <label class="form-check-label" for="include">Include Driver</label>
                                            </div>
                                            <div class="invalid-feedback d-block" v-if="form.errors.mobil_fasilitas">{{ form.errors.mobil_fasilitas }}</div>
                                        </div>

                                        <!-- Conditional Prices -->
                                        <div class="row">
                                            <div class="col-md-6 mb-3" v-if="hasFacility('Lepas Kunci')">
                                                <label for="mobil_harga_sewa" class="form-label">Harga Lepas Kunci</label>
                                                <div class="input-group">
                                                    <span class="input-group-text">Rp</span>
                                                    <input type="text" id="mobil_harga_sewa" name="mobil_harga_sewa" :value="displayHargaSewa" @input="onHargaSewaInput" class="form-control" :class="{ 'is-invalid': form.errors.mobil_harga_sewa }" placeholder="0" autocomplete="off">
                                                </div>
                                            </div>
                                            <div class="col-md-6 mb-3" v-if="hasFacility('Include')">
                                                <label for="mobil_harga_include" class="form-label">Harga Include</label>
                                                <div class="input-group">
                                                    <span class="input-group-text">Rp</span>
                                                    <input type="text" id="mobil_harga_include" name="mobil_harga_include" :value="displayHargaInclude" @input="onHargaIncludeInput" class="form-control" :class="{ 'is-invalid': form.errors.mobil_harga_include }" placeholder="0" autocomplete="off">
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div class="form-group mb-3">
                                             <label for="mobil_detail" class="form-label">Keterangan Singkat</label>
                                             <textarea id="mobil_detail" name="mobil_detail" v-model="form.mobil_detail" class="form-control" rows="3" :class="{ 'is-invalid': form.errors.mobil_detail }" autocomplete="off"></textarea>
                                             <div class="d-flex justify-content-between">
                                                <div class="invalid-feedback d-block" v-if="form.errors.mobil_detail">{{ form.errors.mobil_detail }}</div>
                                                <small class="text-muted ms-auto">{{ detailWordCount }} kata</small>
                                             </div>
                                        </div>

                                        <!-- SEO Meta Data -->
                                        <div class="card bg-light-light mb-0 mt-3">
                                            <div class="card-body py-3">
                                                <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>
                                                
                                                <div class="mb-3">
                                                    <div class="d-flex justify-content-between">
                                                        <label for="meta_title" class="form-label">Meta Title</label>
                                                        <small :class="form.meta_title?.length > 55 ? 'text-danger' : 'text-muted'">{{ form.meta_title?.length || 0 }}/55</small>
                                                    </div>
                                                    <input type="text" id="meta_title" name="meta_title" v-model="form.meta_title" class="form-control" maxlength="55" placeholder="Meta Title" autocomplete="off">
                                                    <small class="text-muted d-block mt-1">Judul yang akan muncul di hasil pencarian Google.</small>
                                                </div>
                                                <div class="mb-0">
                                                    <div class="d-flex justify-content-between">
                                                        <label for="meta_description" class="form-label">Meta Description</label>
                                                        <small :class="form.meta_description?.length > 155 ? 'text-danger' : 'text-muted'">{{ form.meta_description?.length || 0 }}/155</small>
                                                    </div>
                                                    <textarea id="meta_description" name="meta_description" v-model="form.meta_description" class="form-control" rows="3" maxlength="155" placeholder="Meta Description" autocomplete="off"></textarea>
                                                    <small class="text-muted d-block mt-1">Deskripsi singkat yang muncul di bawah judul pada hasil pencarian.</small>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                                <!-- Article Row -->
                                <div class="row mt-3">
                                    <div class="col-12">
                                        <div class="form-group">
                                            <label for="mobil_artikel" class="form-label">Konten Artikel Lengkap <span class="text-danger">*</span></label>
                                            <textarea id="mobil_artikel" name="mobil_artikel" class="form-control" :class="{ 'is-invalid': form.errors.mobil_artikel }"></textarea>
                                            <div class="invalid-feedback d-block" v-if="form.errors.mobil_artikel">{{ form.errors.mobil_artikel }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button type="button" class="btn btn-warning me-2" v-if="isEditing" @click="resetForm"><i class="ti-trash me-2"></i> Batal</button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <i class="ti-save-alt me-2" v-if="!saving"></i>
                                    <i class="fa fa-spinner fa-spin me-2" v-else></i>
                                    <span v-if="saving">Menyimpan...</span>
                                    <span v-else>{{ isEditing ? 'Simpan Perubahan' : 'Simpan' }}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <!-- List Section -->
            <div class="row mt-4">
                <div class="col-12">
                     <div class="box">
                        <div class="box-header with-border d-flex justify-content-between align-items-center">
                            <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Armada</h4>
                            <span v-if="isSavingOrder" class="text-muted small">
                                <span class="spinner-border spinner-border-sm me-1"></span>Menyimpan urutan...
                            </span>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th style="width: 40px;" class="text-center"><i class="fa fa-bars"></i></th>
                                            <th>Foto</th>
                                            <th>Nama / Tipe</th>
                                            <th>Keterangan</th>
                                            <th>Harga</th>
                                            <th>Status</th>
                                            <th class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <draggable 
                                        v-model="sortableMobils" 
                                        tag="tbody" 
                                        item-key="hash_id" 
                                        handle=".drag-handle"
                                        @end="onSortEnd"
                                        :animation="200"
                                    >
                                        <template #item="{element}">
                                            <tr class="align-middle">
                                                <td class="text-center text-muted drag-handle" style="cursor: move;">
                                                    <i class="fa fa-bars"></i>
                                                </td>
                                                <td style="min-width: 140px;">
                                                    <div class="d-flex gap-2">
                                                        <div class="text-center">
                                                            <img v-if="element.mobil_foto" :src="getImageUrl(element.mobil_foto)" class="rounded" style="width: 60px; height: 40px; object-fit: cover;">
                                                            <span v-else class="text-muted small">-</span>
                                                            <div class="small text-muted">Desktop</div>
                                                        </div>
                                                        <div class="text-center">
                                                            <img v-if="element.mobil_foto_mobile" :src="getImageUrl(element.mobil_foto_mobile)" class="rounded" style="width: 60px; height: 40px; object-fit: cover;">
                                                            <span v-else class="text-muted small">-</span>
                                                            <div class="small text-muted">Mobile</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div class="fw-bold">{{ element.mobil_nama }}</div>
                                                    <small class="text-muted">{{ element.jenis_mobil?.jenis_nama }}</small>
                                                </td>
                                                <td style="max-width: 250px;">
                                                    <div class="text-truncate" style="max-width: 250px;" :title="element.mobil_detail">{{ element.mobil_detail || '-' }}</div>
                                                </td>
                                                <td>
                                                    <div v-if="element.mobil_fasilitas && element.mobil_fasilitas.includes('Lepas Kunci')">
                                                        <small>Lepas Kunci:</small> {{ formatCurrency(element.mobil_harga_sewa) }}
                                                    </div>
                                                    <div v-if="element.mobil_fasilitas && element.mobil_fasilitas.includes('Include')">
                                                        <small>Include Driver:</small> {{ formatCurrency(element.mobil_harga_include) }}
                                                    </div>
                                                </td>
                                                <td>
                                                    <button type="button" class="btn btn-sm rounded-1 px-3" :class="element.mobil_status ? 'btn-success' : 'btn-danger'" @click="toggleStatus(element)" :title="element.mobil_status ? 'Nonaktifkan' : 'Aktifkan'" :disabled="togglingId === element.hash_id">
                                                        <span v-if="togglingId === element.hash_id" class="spinner-border spinner-border-sm me-1"></span>
                                                        <i v-else class="fa me-1" :class="element.mobil_status ? 'fa-check-circle' : 'fa-times-circle'"></i>
                                                        {{ element.mobil_status ? 'Aktif' : 'Nonaktif' }}
                                                    </button>
                                                </td>
                                                <td class="text-center" style="width: 150px;">
                                                    <div class="btn-group">
                                                         <button class="btn btn-info btn-sm" @click="viewArtikel(element)" title="Detail"><i class="fa fa-eye"></i></button>
                                                         <button class="btn btn-primary btn-sm" @click="edit(element)" title="Ubah"><i class="fa fa-edit"></i></button>
                                                         <button class="btn btn-danger btn-sm" @click="confirmDelete(element)" title="Hapus"><i class="fa fa-trash"></i></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </draggable>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

         <!-- Delete Confirmation Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title"><i class="fa fa-trash me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus data <strong>{{ deleteName }}</strong>?</p>
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck" @click="submitDelete">Ya, Hapus</button>
                    </div>
                </div>
            </div>
        </div>
        
         <!-- Error/Warning/Success Modal -->
        <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" :class="{
                        'bg-success': modalType === 'success',
                        'bg-warning text-dark': modalType === 'warning',
                        'bg-danger': modalType === 'error'
                    }">
                        <h5 class="modal-title">
                            <i class="me-2" :class="{
                                'fa fa-check-circle': modalType === 'success',
                                'fa fa-exclamation-triangle': modalType === 'warning',
                                'ti-alert': modalType === 'error'
                            }"></i>
                            {{ modalType === 'success' ? 'Berhasil' : (modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi') }}
                        </h5>
                        <button type="button" class="btn-close" :class="{ 'btn-close-white': modalType !== 'warning' }" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                        <p class="mb-3" v-else-if="modalType === 'success'">Operasi berhasil dilakukan:</p>
                        <p class="mb-3" v-else>Informasi sistem:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" :class="{
                                'list-group-item-success': modalType === 'success',
                                'list-group-item-warning': modalType === 'warning',
                                'list-group-item-danger': modalType === 'error'
                            }" v-for="(error, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="{
                                    'fa-check-circle': modalType === 'success',
                                    'fa-info-circle': modalType === 'warning',
                                    'fa-exclamation-circle': modalType === 'error'
                                }"></i>{{ error }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" :class="{
                            'btn-success': modalType === 'success',
                            'btn-warning': modalType === 'warning',
                            'btn-danger': modalType === 'error'
                        }" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- View Artikel Modal -->
        <div class="modal fade" id="viewModal" tabindex="-1" data-bs-backdrop="static">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header bg-primary text-white">
                        <h5 class="modal-title"><i class="fa fa-file-text me-2"></i>{{ modslTitle }}</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <!-- Metadata Info -->
                        <div class="list-group-item list-group-item-info p-3 rounded mb-3">
                            <table class="table table-sm table-borderless mb-0">
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="width: 1%; white-space: nowrap;">Nama Mobil</td>
                                    <td class="text-center" style="width: 1%; white-space: nowrap;">:</td>
                                    <td class="fw-bold text-primary">{{ modalItem.mobil_nama }}</td>
                                </tr>
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Jenis</td>
                                    <td class="text-center">:</td>
                                    <td>{{ modalItem.jenis_mobil?.jenis_nama || '-' }}</td>
                                </tr>
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Slug</td>
                                    <td class="text-center">:</td>
                                    <td class="text-danger">/{{ modalItem.mobil_slug }}</td>
                                </tr>
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Meta Title</td>
                                    <td class="text-center">:</td>
                                    <td>{{ modalItem.meta_title || '-' }}</td>
                                </tr>
                                <tr class="align-top">
                                    <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Meta Description</td>
                                    <td class="text-center">:</td>
                                    <td>{{ modalItem.meta_description || '-' }}</td>
                                </tr>
                            </table>
                        </div>
                        
                        <h5 class="text-primary border-bottom pb-2 mb-3"><i class="ti-file me-2"></i>Konten Artikel</h5>
                        <div v-html="modalItem.mobil_artikel || '<em>Tidak ada konten artikel.</em>'"></div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Tutup</button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>
